/**
 * Read-only Your Carts table.
 *
 * Storefront requests go to the Shopify App Proxy path
 * /apps/aone-api/carts/overview. Shopify signs that request and forwards it
 * to GET /api/shopify/proxy/carts/overview. The browser must not call the
 * Custom App origin directly.
 */
(function () {
  'use strict';

  var DEFAULT_PAGE_SIZE = 10;
  var PAGE_SIZES = [10, 20, 50];
  var SEARCH_DEBOUNCE_MS = 300;
  var SKELETON_ROWS = 6;
  var MISSING = '\u2014';
  var FALLBACK_PATH = '/apps/aone-api/carts/overview';
  var roots = typeof WeakMap === 'function' ? new WeakMap() : null;

  var CART_LABELS = {
    ACTIVE: 'Active',
    EXPIRED: 'Expired',
    COMPLETED: 'Completed',
  };
  var CART_MODIFIERS = {
    ACTIVE: 'is-active',
    EXPIRED: 'is-expired',
    COMPLETED: 'is-completed',
  };
  var HOPSTACK_LABELS = {
    SYNCED: 'Synced',
    PENDING: 'Pending',
    FAILED: 'Failed',
    IN_PROGRESS: 'In Progress',
  };
  var HOPSTACK_MODIFIERS = {
    SYNCED: 'is-synced',
    PENDING: 'is-pending',
    FAILED: 'is-failed',
    IN_PROGRESS: 'is-progress',
  };

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function getRecord(root) {
    if (roots) {
      if (!roots.has(root)) {
        roots.set(root, { seq: 0, controller: null, page: 1, limit: DEFAULT_PAGE_SIZE, search: '', searchTimer: null, highlightKey: '', highlightTimer: null });
      }
      return roots.get(root);
    }
    if (!root.__aoneYourCarts) {
      root.__aoneYourCarts = { seq: 0, controller: null, page: 1, limit: DEFAULT_PAGE_SIZE, search: '', searchTimer: null, highlightKey: '', highlightTimer: null };
    }
    return root.__aoneYourCarts;
  }

  function readConfig(root) {
    var el = qs('[data-aone-your-carts-config]', root);
    if (!(el instanceof HTMLElement)) return {};
    try {
      var parsed = JSON.parse(el.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (err) {
      return {};
    }
  }

  /**
   * Keep only a same-origin App Proxy path. A pasted backend URL is reduced
   * to its pathname, and a private /api route falls back to the storefront path.
   */
  function normalizeOverviewPath(path) {
    var value = String(path || '').trim();
    if (!value) return FALLBACK_PATH;
    if (/^https?:\/\//i.test(value)) {
      try {
        value = new URL(value).pathname || '';
      } catch (err) {
        return FALLBACK_PATH;
      }
    }
    if (value.charAt(0) !== '/') value = '/' + value;
    value = value.replace(/\/+$/, '');
    if (!value || value.indexOf('/api/') === 0) return FALLBACK_PATH;
    return value;
  }

  function salesRepEmail(config) {
    if (!config || config.salesRepEmail == null) return '';
    return String(config.salesRepEmail).trim();
  }

  function setHidden(el, hidden) {
    if (!(el instanceof HTMLElement)) return;
    if (hidden) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  function setBusy(root, busy) {
    if (busy) root.setAttribute('aria-busy', 'true');
    else root.removeAttribute('aria-busy');
  }

  function skeletonRow() {
    var row = document.createElement('tr');
    row.className = 'aone-your-carts__skeleton-row';
    row.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 10; i += 1) {
      var cell = document.createElement('td');
      var bar = document.createElement('span');
      bar.className = 'aone-your-carts__skeleton-bar';
      cell.appendChild(bar);
      row.appendChild(cell);
    }
    return row;
  }

  function showSkeleton(root) {
    var body = qs('[data-aone-your-carts-body]', root);
    var wrap = qs('[data-aone-your-carts-table-wrap]', root);
    if (body) {
      body.replaceChildren();
      for (var i = 0; i < SKELETON_ROWS; i += 1) body.appendChild(skeletonRow());
    }
    setHidden(wrap, false);
    setHidden(qs('[data-aone-your-carts-empty]', root), true);
    setHidden(qs('[data-aone-your-carts-error]', root), true);
    setHidden(qs('[data-aone-your-carts-gate]', root), true);
    setHidden(qs('[data-aone-your-carts-pager]', root), true);
    setBusy(root, true);
  }

  function showGate(root) {
    setHidden(qs('[data-aone-your-carts-table-wrap]', root), true);
    setHidden(qs('[data-aone-your-carts-empty]', root), true);
    setHidden(qs('[data-aone-your-carts-error]', root), true);
    setHidden(qs('[data-aone-your-carts-pager]', root), true);
    setHidden(qs('[data-aone-your-carts-gate]', root), false);
    setBusy(root, false);
  }

  function showEmpty(root, page, totalPages) {
    var record = getRecord(root);
    var searching = Boolean(record.search);
    var title = qs('[data-aone-your-carts-empty-title]', root);
    var text = qs('[data-aone-your-carts-empty-text]', root);
    if (title) title.textContent = 'No carts found';
    if (text) {
      text.textContent = searching
        ? 'No carts match your search.'
        : 'Your carts will appear here when available.';
    }
    setHidden(qs('[data-aone-your-carts-table-wrap]', root), true);
    setHidden(qs('[data-aone-your-carts-error]', root), true);
    setHidden(qs('[data-aone-your-carts-gate]', root), true);
    setHidden(qs('[data-aone-your-carts-empty]', root), false);
    renderPager(root, page || 1, totalPages || 0);
    setBusy(root, false);
  }

  function showError(root, message) {
    var messageEl = qs('[data-aone-your-carts-error-message]', root);
    if (messageEl) messageEl.textContent = message || 'Please try again.';
    setHidden(qs('[data-aone-your-carts-table-wrap]', root), true);
    setHidden(qs('[data-aone-your-carts-empty]', root), true);
    setHidden(qs('[data-aone-your-carts-gate]', root), true);
    setHidden(qs('[data-aone-your-carts-pager]', root), true);
    setHidden(qs('[data-aone-your-carts-error]', root), false);
    setBusy(root, false);
  }

  function textOrNull(value) {
    if (value == null) return null;
    var text = String(value).trim();
    return text ? text : null;
  }

  function humanizeStatus(value) {
    return String(value)
      .trim()
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/\b[a-z]/g, function (char) {
        return char.toUpperCase();
      });
  }

  function statusKey(value) {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');
  }

  function statusPresentation(value, labels, modifiers) {
    var text = textOrNull(value);
    if (!text) return null;
    var key = statusKey(text);
    return {
      label: Object.prototype.hasOwnProperty.call(labels, key) ? labels[key] : humanizeStatus(key),
      modifier: Object.prototype.hasOwnProperty.call(modifiers, key) ? modifiers[key] : 'is-fallback',
    };
  }

  function formatItems(count) {
    if (count == null || count === '') return null;
    var amount = Number(count);
    if (!Number.isFinite(amount)) return null;
    var whole = Math.trunc(amount);
    return {
      count: String(whole),
      label: whole === 1 ? 'item' : 'items',
    };
  }

  function formatWeight(weight, unit) {
    var text = textOrNull(weight);
    if (!text) return null;
    var rawUnit = textOrNull(unit) || 'g';
    var normalized = rawUnit.toLowerCase();
    var suffix =
      normalized === 'g' || normalized === 'gm' || normalized === 'gram' || normalized === 'grams'
        ? 'gm'
        : rawUnit;
    return { amount: text, unit: suffix };
  }

  function formatAmount(amount, currency) {
    var text = textOrNull(amount);
    if (!text) return null;
    var numeric = Number(text.replace(/,/g, ''));
    if (!Number.isFinite(numeric)) return null;
    var code = textOrNull(currency) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numeric);
    } catch (err) {
      return code + ' ' + numeric.toFixed(2);
    }
  }

  function formatDate(value) {
    var text = textOrNull(value);
    if (!text) return null;
    var date = new Date(text);
    if (Number.isNaN(date.getTime())) return null;
    return {
      date: new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date),
      time: new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(date),
    };
  }

  function appendText(parent, value, className) {
    var node = document.createElement('span');
    if (className) node.className = className;
    node.textContent = value;
    parent.appendChild(node);
    return node;
  }

  function fillDash(cell) {
    cell.textContent = MISSING;
  }

  function fillDate(cell, value) {
    var formatted = formatDate(value);
    if (!formatted) {
      fillDash(cell);
      return;
    }
    var wrap = document.createElement('span');
    wrap.className = 'aone-your-carts__date';
    appendText(wrap, formatted.date);
    appendText(wrap, formatted.time, 'aone-your-carts__date-time');
    cell.appendChild(wrap);
  }

  function fillBadge(cell, presentation, emptyLabel) {
    if (!presentation) {
      cell.textContent = emptyLabel || MISSING;
      return;
    }
    var badge = document.createElement('span');
    badge.className = 'aone-your-carts__badge ' + presentation.modifier;
    badge.textContent = presentation.label;
    cell.appendChild(badge);
  }

  function normalizeCartKey(value) {
    var text = String(value || '').trim().toUpperCase();
    if (!text) return '';
    if (text.indexOf('CART-') === 0) text = text.slice(5);
    var stripped = text.replace(/^0+/, '');
    return stripped || text;
  }

  function clearHighlightTimer(record) {
    if (record.highlightTimer) {
      clearTimeout(record.highlightTimer);
      record.highlightTimer = null;
    }
  }

  function applyHighlight(root) {
    var record = getRecord(root);
    var key = record.highlightKey;
    if (!key) return false;
    var matched = false;
    var rows = root.querySelectorAll('[data-cart-key]');
    Array.prototype.forEach.call(rows, function (row) {
      if (row.getAttribute('data-cart-key') === key) {
        row.classList.add('is-just-synced');
        matched = true;
      }
    });
    if (!matched) return false;
    record.highlightKey = '';
    clearHighlightTimer(record);
    record.highlightTimer = setTimeout(function () {
      record.highlightTimer = null;
      var highlighted = root.querySelectorAll('.is-just-synced');
      Array.prototype.forEach.call(highlighted, function (row) {
        row.classList.remove('is-just-synced');
      });
    }, 12000);
    return true;
  }

  function buildRow(cart, currencyCode) {
    var row = document.createElement('tr');
    var cartNo = document.createElement('td');
    var cartNumber = textOrNull(cart.cartNumber);
    var cartKey = normalizeCartKey(cartNumber);
    if (cartKey) row.setAttribute('data-cart-key', cartKey);
    if (cartNumber) appendText(cartNo, cartNumber, 'aone-your-carts__cart-no');
    else fillDash(cartNo);

    var customer = document.createElement('td');
    var customerName = textOrNull(cart.customerName);
    if (customerName) appendText(customer, customerName, 'aone-your-carts__customer');
    else fillDash(customer);

    var customerNo = document.createElement('td');
    var customerNumber = textOrNull(cart.customerNumber);
    if (customerNumber) customerNo.textContent = customerNumber;
    else fillDash(customerNo);

    var items = document.createElement('td');
    var itemValue = formatItems(cart.itemsCount);
    if (itemValue) {
      items.appendChild(document.createTextNode(itemValue.count + ' '));
      appendText(items, itemValue.label, 'aone-your-carts__muted');
    } else {
      fillDash(items);
    }

    var weight = document.createElement('td');
    var weightValue = formatWeight(cart.totalWeight, cart.weightUnit);
    if (weightValue) {
      weight.appendChild(document.createTextNode(weightValue.amount + ' '));
      appendText(weight, weightValue.unit, 'aone-your-carts__muted');
    } else {
      fillDash(weight);
    }

    var amount = document.createElement('td');
    var amountValue = formatAmount(cart.totalAmount, cart.currency || currencyCode);
    if (amountValue) appendText(amount, amountValue, 'aone-your-carts__amount');
    else fillDash(amount);

    var created = document.createElement('td');
    fillDate(created, cart.createdAt);
    var expires = document.createElement('td');
    fillDate(expires, cart.expiresAt);

    var cartStatus = document.createElement('td');
    cartStatus.className = 'is-center';
    fillBadge(cartStatus, statusPresentation(cart.cartStatus, CART_LABELS, CART_MODIFIERS));

    var hopstack = document.createElement('td');
    hopstack.className = 'is-center';
    fillBadge(
      hopstack,
      statusPresentation(cart.hopstackStatus, HOPSTACK_LABELS, HOPSTACK_MODIFIERS),
      'N/A'
    );

    [cartNo, customer, customerNo, items, weight, amount, created, expires, cartStatus, hopstack].forEach(
      function (cell) {
        row.appendChild(cell);
      }
    );
    return row;
  }

  function pageWindow(page, totalPages) {
    if (totalPages <= 7) {
      var all = [];
      for (var index = 1; index <= totalPages; index += 1) all.push(index);
      return all;
    }
    var pages = [1];
    var start = Math.max(2, page - 1);
    var end = Math.min(totalPages - 1, page + 1);
    if (start > 2) pages.push('gap');
    for (var number = start; number <= end; number += 1) pages.push(number);
    if (end < totalPages - 1) pages.push('gap');
    pages.push(totalPages);
    return pages;
  }

  function fillPageButtons(container, page, totalPages) {
    container.replaceChildren();
    pageWindow(page, totalPages).forEach(function (entry) {
      if (entry === 'gap') {
        var gap = document.createElement('span');
        gap.className = 'aone-your-carts__pager-gap';
        gap.setAttribute('aria-hidden', 'true');
        gap.textContent = '\u2026';
        container.appendChild(gap);
        return;
      }
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'aone-your-carts__pager-btn aone-your-carts__pager-num';
      button.setAttribute('data-aone-your-carts-page', String(entry));
      button.textContent = String(entry);
      if (entry === page) {
        button.classList.add('is-current');
        button.setAttribute('aria-current', 'page');
        button.disabled = true;
      }
      container.appendChild(button);
    });
  }

  function renderPager(root, page, totalPages) {
    var pager = qs('[data-aone-your-carts-pager]', root);
    var label = qs('[data-aone-your-carts-page-label]', root);
    var prev = qs('[data-aone-your-carts-prev]', root);
    var next = qs('[data-aone-your-carts-next]', root);
    var pages = qs('[data-aone-your-carts-pages]', root);
    if (!(pager instanceof HTMLElement)) return;
    if (!Number.isFinite(totalPages) || totalPages <= 1) {
      if (pages) pages.replaceChildren();
      setHidden(pager, true);
      return;
    }
    if (label) label.textContent = 'Page ' + page + ' of ' + totalPages;
    if (prev instanceof HTMLButtonElement) prev.disabled = page <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = page >= totalPages;
    if (pages) fillPageButtons(pages, page, totalPages);
    setHidden(pager, false);
  }

  function renderCarts(root, carts, currencyCode, page, totalPages) {
    var body = qs('[data-aone-your-carts-body]', root);
    var wrap = qs('[data-aone-your-carts-table-wrap]', root);
    if (!(body instanceof HTMLElement)) return;
    body.replaceChildren();
    carts.forEach(function (cart) {
      body.appendChild(buildRow(cart && typeof cart === 'object' ? cart : {}, currencyCode));
    });
    setHidden(wrap, false);
    setHidden(qs('[data-aone-your-carts-empty]', root), true);
    setHidden(qs('[data-aone-your-carts-error]', root), true);
    setHidden(qs('[data-aone-your-carts-gate]', root), true);
    renderPager(root, page, totalPages);
    setBusy(root, false);
  }

  function errorMessage(status, payload) {
    var apiMessage =
      payload && typeof payload.message === 'string' && payload.message.trim() ? payload.message.trim() : '';
    if (status === 401) {
      return 'Your session could not be verified. Refresh the page and try again.';
    }
    if (status === 403) {
      return apiMessage || 'You are not authorized to view these carts.';
    }
    return apiMessage || 'Unable to load your carts. Please try again.';
  }

  function readOverview(payload) {
    if (!payload || payload.success !== true || !payload.data || !Array.isArray(payload.data.carts)) {
      return null;
    }
    var pagination = payload.data.pagination && typeof payload.data.pagination === 'object' ? payload.data.pagination : {};
    var page = Number(pagination.page);
    var totalPages = Number(pagination.totalPages);
    return {
      carts: payload.data.carts,
      page: Number.isInteger(page) && page > 0 ? page : 1,
      totalPages: Number.isInteger(totalPages) && totalPages >= 0 ? totalPages : payload.data.carts.length ? 1 : 0,
    };
  }

  function readPageSize(root) {
    var select = qs('[data-aone-your-carts-limit]', root);
    var value = select ? Number(select.value) : DEFAULT_PAGE_SIZE;
    return PAGE_SIZES.indexOf(value) === -1 ? DEFAULT_PAGE_SIZE : value;
  }

  function buildOverviewUrl(path, email, page, limit, search) {
    var params = new URLSearchParams();
    params.set('salesRepEmail', email);
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', search);
    return path + '?' + params.toString();
  }

  function loadPageResult(ok, matched, stale) {
    return { ok: ok, matched: !!matched, stale: !!stale };
  }

  async function loadPage(root, config, page) {
    var record = getRecord(root);
    var email = salesRepEmail(config);
    if (!email) {
      showGate(root);
      return loadPageResult(false, false, false);
    }

    var requestedPage = Number.isInteger(page) && page > 0 ? page : 1;
    record.page = requestedPage;
    record.limit = readPageSize(root);
    if (record.controller) record.controller.abort();
    record.controller = typeof AbortController === 'function' ? new AbortController() : null;
    record.seq += 1;
    var seq = record.seq;
    showSkeleton(root);

    var url = buildOverviewUrl(
      normalizeOverviewPath(config.overviewPath),
      email,
      requestedPage,
      record.limit,
      record.search
    );
    try {
      var response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: record.controller ? record.controller.signal : undefined,
      });
      var payload = {};
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = {};
      }
      if (seq !== record.seq) return loadPageResult(false, false, true);
      if (!response.ok) {
        showError(root, errorMessage(response.status, payload));
        return loadPageResult(false, false, false);
      }
      var overview = readOverview(payload);
      if (!overview) {
        showError(root, 'Cart overview response was incomplete. Please try again.');
        return loadPageResult(false, false, false);
      }
      if (!overview.carts.length) {
        showEmpty(root, overview.page, overview.totalPages);
        return loadPageResult(true, false, false);
      }
      renderCarts(
        root,
        overview.carts,
        textOrNull(config.currencyCode) || 'USD',
        overview.page,
        overview.totalPages
      );
      return loadPageResult(true, applyHighlight(root), false);
    } catch (err) {
      if (seq !== record.seq) return loadPageResult(false, false, true);
      if (err && err.name === 'AbortError') return loadPageResult(false, false, true);
      showError(root, 'Unable to load your carts. Please try again.');
      return loadPageResult(false, false, false);
    }
  }

  function eachCartRoot(callback) {
    var nodes = document.querySelectorAll('[data-aone-your-carts]');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node instanceof HTMLElement) callback(node);
    });
  }

  async function refreshCartTable(highlightLabel) {
    var key = normalizeCartKey(highlightLabel);
    var jobs = [];
    eachCartRoot(function (root) {
      var record = getRecord(root);
      var config = readConfig(root);
      clearHighlightTimer(record);
      record.highlightKey = key;
      var page = record.page || 1;
      jobs.push(loadPage(root, config, page).then(function (result) {
        if (!result || result.stale || !result.ok) return result;
        if (result.matched || page === 1) {
          if (!result.matched) record.highlightKey = '';
          return result;
        }
        record.highlightKey = key;
        return loadPage(root, config, 1).then(function (nextResult) {
          if (nextResult && nextResult.ok && !nextResult.matched) record.highlightKey = '';
          return nextResult;
        });
      }));
    });
    await Promise.all(jobs);
  }

  function focusCartRow(highlightLabel) {
    var key = normalizeCartKey(highlightLabel);
    if (!key) return;
    eachCartRoot(function (root) {
      var record = getRecord(root);
      var config = readConfig(root);
      var escaped = window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(key) : key;
    var row = root.querySelector('[data-cart-key="' + escaped + '"]');
      if (row instanceof HTMLElement) {
        record.highlightKey = key;
        applyHighlight(root);
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      var searchInput = qs('[data-aone-your-carts-search]', root);
      if (searchInput) searchInput.value = key;
      record.search = key;
      record.highlightKey = key;
      loadPage(root, config, 1).then(function () {
        var found = root.querySelector('[data-cart-key="' + escaped + '"]');
        if (found instanceof HTMLElement) found.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  function bind(root) {
    if (root.getAttribute('data-aone-your-carts-ready') === 'true') return;
    root.setAttribute('data-aone-your-carts-ready', 'true');
    var config = readConfig(root);
    var retry = qs('[data-aone-your-carts-retry]', root);
    var prev = qs('[data-aone-your-carts-prev]', root);
    var next = qs('[data-aone-your-carts-next]', root);
    var pages = qs('[data-aone-your-carts-pages]', root);
    var searchInput = qs('[data-aone-your-carts-search]', root);
    var limitSelect = qs('[data-aone-your-carts-limit]', root);

    function queueSearch() {
      var record = getRecord(root);
      if (record.searchTimer) clearTimeout(record.searchTimer);
      record.searchTimer = setTimeout(function () {
        record.searchTimer = null;
        var nextSearch = searchInput ? searchInput.value.trim() : '';
        if (nextSearch === record.search) return;
        record.search = nextSearch;
        loadPage(root, config, 1);
      }, SEARCH_DEBOUNCE_MS);
    }

    if (retry) {
      retry.addEventListener('click', function () {
        loadPage(root, config, getRecord(root).page || 1);
      });
    }
    if (prev) {
      prev.addEventListener('click', function () {
        var current = getRecord(root).page || 1;
        if (current > 1) loadPage(root, config, current - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        loadPage(root, config, (getRecord(root).page || 1) + 1);
      });
    }
    if (pages) {
      pages.addEventListener('click', function (event) {
        var button = event.target;
        if (!(button instanceof HTMLButtonElement)) return;
        var nextPage = Number(button.getAttribute('data-aone-your-carts-page'));
        if (!Number.isInteger(nextPage) || nextPage < 1 || button.disabled) return;
        loadPage(root, config, nextPage);
      });
    }
    if (searchInput) searchInput.addEventListener('input', queueSearch);
    if (limitSelect) {
      limitSelect.addEventListener('change', function () {
        var record = getRecord(root);
        var nextLimit = readPageSize(root);
        if (nextLimit === record.limit) return;
        record.limit = nextLimit;
        loadPage(root, config, 1);
      });
    }

    loadPage(root, config, 1);
  }

  function boot(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var nodes = context.querySelectorAll('[data-aone-your-carts]');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node instanceof HTMLElement) bind(node);
    });
  }

  function abortIn(scope) {
    var context = scope && scope.querySelectorAll ? scope : null;
    if (!context) return;
    Array.prototype.forEach.call(context.querySelectorAll('[data-aone-your-carts]'), function (node) {
      if (!(node instanceof HTMLElement)) return;
      var record = roots ? roots.get(node) : node.__aoneYourCarts;
      if (!record) return;
      if (record.searchTimer) clearTimeout(record.searchTimer);
      clearHighlightTimer(record);
      if (record.controller) record.controller.abort();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target instanceof HTMLElement ? event.target : document);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    abortIn(event.target instanceof HTMLElement ? event.target : null);
  });

  window.AoneYourCarts = {
    refresh: refreshCartTable,
    focusCart: focusCartRow,
  };
})();
